import { AccountInfo } from '@azure/msal-browser';

export interface UserRegistrationPrefill {
  mail: string;
  firstName: string;
  name: string;
}

const MAIL_CLAIMS = [
  'emails',
  'email',
  'signInNames.emailAddress',
  'preferred_username',
  'upn',
  'unique_name'
];
const FIRST_NAME_CLAIMS = ['given_name', 'givenName', 'first_name', 'firstName'];
const LAST_NAME_CLAIMS = ['family_name', 'familyName', 'surname', 'last_name', 'lastName'];
const DISPLAY_NAME_CLAIMS = ['name', 'displayName'];

const EMPTY_PREFILL: UserRegistrationPrefill = { mail: '', firstName: '', name: '' };

/**
 * Derives the pre-fill values for the user registration form from the id token
 * claims, falling back to the data MSAL put on the account itself.
 *
 * Everything is best effort: whatever cannot be resolved is returned as an
 * empty string so the field can be filled in manually.
 */
export function buildUserRegistrationPrefill(
  claims: Record<string, unknown> | null | undefined,
  account: AccountInfo | null
): UserRegistrationPrefill {
  const safeClaims = claims ?? {};

  const mail = pickMail(safeClaims, account);
  let firstName = readClaim(safeClaims, FIRST_NAME_CLAIMS);
  let name = readClaim(safeClaims, LAST_NAME_CLAIMS);

  if (!firstName && !name) {
    const displayName = readClaim(safeClaims, DISPLAY_NAME_CLAIMS) || asString(account?.name);
    [firstName, name] = splitDisplayName(displayName);
  }

  return { ...EMPTY_PREFILL, mail, firstName, name };
}

function pickMail(claims: Record<string, unknown>, account: AccountInfo | null): string {
  const candidate = readClaim(claims, MAIL_CLAIMS) || asString(account?.username);
  return candidate.includes('@') ? candidate : '';
}

function readClaim(claims: Record<string, unknown>, keys: Array<string>): string {
  for (const key of keys) {
    const value = claims[key];
    const resolved = Array.isArray(value) ? asString(value[0]) : asString(value);

    if (resolved) {
      return resolved;
    }
  }

  return '';
}

function splitDisplayName(displayName: string): [string, string] {
  const parts = displayName.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return ['', ''];
  }

  if (parts.length === 1) {
    return [parts[0], ''];
  }

  return [parts[0], parts.slice(1).join(' ')];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
